// /turnout — deep-dive page (Stage 2, Part 5).
//
// Four sections:
//   1. Hero with the latest national figure + the editor's label
//      (or a "figures expected ~2pm" placeholder before any save).
//   2. Per-district cube grid (the polls-day "where are we now"
//      reader).
//   3. Consolidated national + district turnout history chart
//      (TurnoutHistoryUnified). Single interactive chart with a
//      2pm / closing toggle, clickable per-district legend, group
//      presets, hover tooltips, and stock-app-style year-range
//      zoom via a draggable brush at the bottom.
//   4. Per-district historical table: 2003 → 2026 columns + a
//      vs-2022 delta. Rows tinted by 2022 margin of victory via
//      marginToColor (same palette as the cubes), and an
//      independent 2pm / closing toggle so the table can sit on a
//      different reading than the chart.
//
// Live 2026 figures come from useElectionDayTurnout (kind='turnout'
// Postgres row, edge-cached via /api/electionday-state). Historical
// years are static.

import { useEffect, useRef, useState } from 'react';
import Header from '../Header';
import Footer from '../Footer';
import TurnoutHistoryUnified from './TurnoutHistoryUnified';
import TurnoutCubeSection from './TurnoutCubeSection';
import { useElectionDayTurnout } from '../../lib/useElectionDayTurnout';
import {
  TURNOUT_DISTRICT_KEYS,
  hasTurnoutData,
} from '../../data/electionday-turnout-types';
import { TURNOUT_HISTORY, type ElectionYear } from '../../data/turnoutHistory';
import { MAP_DATA, marginToColor } from '../../lib/buildMapPaths';
import historyJson from '../../data/district_turnout_history.json';
import './TurnoutPage.css';

const CLOSE_HISTORY = historyJson as Record<string, Record<string, number>>;
const HISTORY_YEARS: ElectionYear[] = [2003, 2008, 2013, 2017, 2022];

type TableWave = 'pct_2pm' | 'pct_close';

/** Read one (district, year, wave) cell — falls back across the two
 *  data sources so closing values from JSON are preferred, and 2pm
 *  values come from the typed TURNOUT_HISTORY module. Returns null
 *  when the figure isn't known (e.g. 2017 closing nationally was
 *  never released by the commission). */
function readHistorical(
  districtKey: string,
  year: ElectionYear,
  wave: TableWave,
): number | null {
  if (wave === 'pct_2pm') {
    const id = Number(districtKey.replace('District ', ''));
    const v = TURNOUT_HISTORY[year]?.[id]?.pct_2pm;
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
  }
  const fromJson = CLOSE_HISTORY[districtKey]?.[String(year)];
  if (typeof fromJson === 'number' && Number.isFinite(fromJson)) return fromJson;
  const id = Number(districtKey.replace('District ', ''));
  const fromHist = TURNOUT_HISTORY[year]?.[id]?.pct_close;
  return typeof fromHist === 'number' && Number.isFinite(fromHist) ? fromHist : null;
}

export default function TurnoutPage() {
  const { data: turnout } = useElectionDayTurnout();
  const hasData = hasTurnoutData(turnout);
  // Wave toggle for the by-district table. Defaults to the latest
  // 2026 reading available — closing the moment any district has a
  // 10pm figure, else 2pm. Pinned once the editor clicks a tab so a
  // late-arriving 10pm reading doesn't yank their 2pm view away.
  // Independent from the history chart's toggle.
  const [tableWave, setTableWave] = useState<TableWave>('pct_2pm');
  const tableWavePinnedRef = useRef(false);
  useEffect(() => {
    if (tableWavePinnedRef.current) return;
    const has10pm = Object.values(turnout.districts ?? {}).some(
      (d) => d?.pct_10pm !== null && d?.pct_10pm !== undefined,
    );
    setTableWave(has10pm ? 'pct_close' : 'pct_2pm');
  }, [turnout]);
  function handleTableWave(next: TableWave): void {
    tableWavePinnedRef.current = true;
    setTableWave(next);
  }

  const headlinePct = turnout.national.pct_10pm ?? turnout.national.pct_2pm;
  const headlineKicker = turnout.label
    ?? (turnout.national.pct_10pm !== null ? 'Final' : 'Live');

  return (
    <>
      <Header />
      <main className="turnout-page">
        <div className="turnout-page__hero">
          <p className="turnout-page__kicker">Lovin Malta · Election 2026</p>
          <h1 className="turnout-page__title">Turnout</h1>
          <p className="turnout-page__lede">
            Official turnout figures by 2pm.
          </p>

          {hasData && headlinePct !== null ? (
            <div className="turnout-page__headline">
              <div className="turnout-page__headline-figure">
                <p className="turnout-page__headline-kicker">
                  National turnout · {headlineKicker}
                </p>
                <p className="turnout-page__headline-number">
                  {headlinePct.toFixed(1)}
                  <span className="turnout-page__headline-percent">%</span>
                </p>
              </div>
              <p className="turnout-page__headline-meta">
                {formatBothReadings(turnout)}
                {turnout.note && (
                  <>
                    {' · '}
                    <span className="turnout-page__headline-note">
                      {turnout.note}
                    </span>
                  </>
                )}
              </p>
            </div>
          ) : (
            <p className="turnout-page__headline-placeholder">
              First turnout figures expected around 14:00 on Saturday.
            </p>
          )}
        </div>

        <section
          className="turnout-page__section"
          aria-labelledby="turnout-cubes-heading"
        >
          <h2 id="turnout-cubes-heading" className="turnout-page__section-title">
            By district · 2026 vs 2022
          </h2>
          <p className="turnout-page__section-lede">
            One row per district. The coloured bar is today's 2026
            turnout; the gray bar shows where 2022 closed at the same
            reading. Bar colour represents direction and scale of 2022
            result.
          </p>
          <TurnoutCubeSection />
        </section>

        <section className="turnout-page__section" aria-labelledby="turnout-history-heading">
          <h2 id="turnout-history-heading" className="turnout-page__section-title">
            National turnout history
          </h2>
          {/* One consolidated chart — national average + 13 district
              lines + wave toggle + per-district click-to-toggle +
              preset groups + hover tooltips + brush-zoom. Replaces
              the prior split between a national-only chart and a
              separate districts chart. */}
          <TurnoutHistoryUnified />
        </section>

        <section
          className="turnout-page__section"
          aria-labelledby="turnout-by-district-heading"
        >
          <div className="turnout-page__table-head">
            <div>
              <h2
                id="turnout-by-district-heading"
                className="turnout-page__section-title"
              >
                By district · 2003 → 2026
              </h2>
              <p className="turnout-page__section-lede">
                Each district's turnout across the last five general
                elections, with the 2026 reading once the newsroom enters
                it. Toggle between the mid-day (2pm) and closing readings
                — the vs-2022 delta is the change in percentage points
                from 2022's matching reading (green if turnout is up, red
                if it's down).
              </p>
            </div>
            <div
              className="turnout-page__wave-toggle"
              role="tablist"
              aria-label="Reading"
            >
              <button
                type="button"
                role="tab"
                aria-selected={tableWave === 'pct_2pm'}
                className={`turnout-page__wave-tab${tableWave === 'pct_2pm' ? ' is-active' : ''}`}
                onClick={() => handleTableWave('pct_2pm')}
              >
                2pm
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tableWave === 'pct_close'}
                className={`turnout-page__wave-tab${tableWave === 'pct_close' ? ' is-active' : ''}`}
                onClick={() => handleTableWave('pct_close')}
              >
                Closing
              </button>
            </div>
          </div>

          <div className="turnout-page__table-wrap">
            <table className="turnout-page__table">
              <thead>
                <tr>
                  <th scope="col" className="turnout-page__th-district">
                    District
                  </th>
                  {HISTORY_YEARS.map((y) => (
                    <th key={y} scope="col">
                      {y}
                    </th>
                  ))}
                  <th scope="col" className="turnout-page__th-live">
                    2026
                  </th>
                  <th scope="col" className="turnout-page__th-delta">
                    vs 2022
                  </th>
                </tr>
              </thead>
              <tbody>
                {TURNOUT_DISTRICT_KEYS.map((key) => {
                  const districtId = Number(key.replace('District ', ''));
                  const path = MAP_DATA.paths.find((p) => p.id === districtId);
                  const margin = path?.properties.results_2022.margin_pp ?? 0;
                  const rowColor = marginToColor(margin);

                  const district = turnout.districts[key];
                  const live =
                    tableWave === 'pct_close'
                      ? (district?.pct_10pm ?? null)
                      : (district?.pct_2pm ?? null);
                  const baseline2022 = readHistorical(key, 2022, tableWave);
                  const delta =
                    live !== null && baseline2022 !== null
                      ? live - baseline2022
                      : null;
                  return (
                    <tr
                      key={key}
                      className="turnout-page__table-row--tinted"
                      style={{ ['--row-color' as string]: rowColor } as React.CSSProperties}
                    >
                      <th scope="row" className="turnout-page__td-district">
                        {key}
                      </th>
                      {HISTORY_YEARS.map((y, yi) => {
                        const v = readHistorical(key, y, tableWave);
                        const prev = yi > 0
                          ? readHistorical(key, HISTORY_YEARS[yi - 1], tableWave)
                          : null;
                        return (
                          <td key={y}>
                            {v !== null ? (
                              <>
                                {v.toFixed(1)}%
                                <CellDirection current={v} previous={prev} />
                              </>
                            ) : '—'}
                          </td>
                        );
                      })}
                      <td className="turnout-page__td-live">
                        {live !== null ? (
                          <>
                            <strong>{live.toFixed(1)}%</strong>
                            <CellDirection current={live} previous={baseline2022} />
                          </>
                        ) : (
                          <span className="turnout-page__td-muted">—</span>
                        )}
                      </td>
                      <td className="turnout-page__td-delta">
                        {delta === null ? (
                          <span className="turnout-page__td-muted">—</span>
                        ) : (
                          <span
                            className={`turnout-page__delta turnout-page__delta--${
                              delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat'
                            }`}
                          >
                            {delta > 0 ? '▲' : delta < 0 ? '▼' : '·'}{' '}
                            {Math.abs(delta).toFixed(1)} pp
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <p className="turnout-page__back">
          <a href="/">← Back to the homepage</a>
        </p>
      </main>
      <Footer />
    </>
  );
}

// ── helpers ──────────────────────────────────────────────────────

/** Small per-cell direction arrow next to each year's % in the
 *  table — ▲ when this year's reading is higher than the previous
 *  year's, ▼ when lower, nothing when it's the first column or
 *  either value is missing. Colour-coded green/red so direction
 *  reads at a glance. ±0.05pp counts as flat (no arrow). */
function CellDirection({
  current,
  previous,
}: {
  current: number;
  previous: number | null;
}) {
  if (previous === null) return null;
  const diff = current - previous;
  if (diff > 0.05) {
    return (
      <span
        className="turnout-page__cell-arrow turnout-page__cell-arrow--up"
        aria-hidden="true"
      >
        {' '}▲
      </span>
    );
  }
  if (diff < -0.05) {
    return (
      <span
        className="turnout-page__cell-arrow turnout-page__cell-arrow--down"
        aria-hidden="true"
      >
        {' '}▼
      </span>
    );
  }
  return null;
}

function formatBothReadings(turnout: {
  national: { pct_2pm: number | null; pct_10pm: number | null };
}): string {
  const parts: string[] = [];
  if (turnout.national.pct_2pm !== null) {
    parts.push(`2pm ${turnout.national.pct_2pm.toFixed(1)}%`);
  }
  if (turnout.national.pct_10pm !== null) {
    parts.push(`10pm ${turnout.national.pct_10pm.toFixed(1)}%`);
  }
  return parts.length > 0 ? parts.join(' · ') : '';
}
